import forEachSelector from '../elements/forEachSelector';
import addStartupScript from '../events/addStartupScript';
import { selectors } from './elements/constants';
import getFieldValue from './elements/getFieldValue';
import getForm from './elements/getForm';
import VMasker from 'vanilla-masker';
import addTargetEventListener from '../events/addTargetEventListener';
import { events } from './models/events';
import intlTelInput from 'intl-tel-input';
import getFieldByName from './elements/getFieldByName';
import { phoneNumberFormat, phoneDialCode, phonePrefix } from './validation/country_lib';

export function phoneNumberMask(countryCode) {
  switch(countryCode) {
    case 'US':
    case 'CA':
      return '(999) 999-9999';
    case 'GB':
      return '99999 999 999';
  }
}

export function phoneNumberMaxLen(countyCode) {
  if (countyCode == 'US' || countyCode == 'CA' ) return 14;
  return 15;
}

function setupPhoneNumberField(field) {
  const form = getForm(field);
  const input = field.querySelector(selectors.fields.join());
  const errorTag = field.querySelector(selectors.formFieldErrorTag);

  if(input) {
    if(field.dataset.international) {
      setupIntlPhoneNumber(input, form);

      // intl-tel-input changes HTML, so move error tag back into place after input
      if(errorTag) { input.parentNode.appendChild(errorTag); }
    } else {
      applyFormatting(input);
    }
  }
}

/**
 * Applies or updates the formatting mask and length on the phone.
 * @param {Element} input - The phone number input element
 */
function applyFormatting(input) {
  const form = getForm(input);
  const countryCode = getFieldValue(form, 'country_code') || 'US';
  const mask = phoneNumberMask(countryCode);

  if(!input || !form) { return; }

  input.maxLength = phoneNumberMaxLen(countryCode);

  if (mask) {
    VMasker(input).maskPattern(mask);
  } else {
    VMasker(input).unMask();
  }
}

function buildIntlInput(input, form) {
  const countryCode = getFieldValue(form, 'country_code') || 'US';

  return intlTelInput(input, {
    initialCountry: countryCode,
    preferredCountries: [countryCode] // countries that appear at the top of the list
  });
}
/**
 * Sets up a phone number input to work with the intl-tel-input library.
 *
 * @param {Element} input - The phone number input element
 * @param {Element} form - The form containing the input
 */
function setupIntlPhoneNumber(input, form) {
  const iti = buildIntlInput(input, form);

  // Update the country_code hidden input when the user changes their phone country
  input.addEventListener('countrychange', function (event) {
    const countryCodeField = getFieldByName(form, 'country_code');

    if(countryCodeField) {
      countryCodeField.value = (iti.getSelectedCountryData().iso2 || 'US').toUpperCase();
    }

    // if country has changed, validation may be affected
    validateInput(event);
  });

  input.addEventListener('input', function (event) {
    if (input.value.match(/^00/)) {
      input.value = input.value.replace(/^00/, '+');
    }
    input.dataset.dialCode = `+${iti.getSelectedCountryData().dialCode || '1'}`;
  });
}

/**
 * Strip phone value of double country code if present
 */
export function stripDoubleDialCode(value, libDialCode, countryCode) {
  let phone = value;
  if ( phone[0] == '+') {
    return phone;
  } else {
    phone = stripPrefix(phone, countryCode);
    const dialLen = libDialCode.toString().length;
    const firstDigits = phone.slice(0, dialLen);
    if (dialLen >= 1 && dialLen <= 3 && libDialCode == firstDigits) {
      phone = phone.slice(dialLen);
    }
    return '+' + libDialCode + phone;
  }
}

/**
 * Strip phone value of prefix if present
 */
function stripPrefix(value, countryCode) {
  const prefix = phonePrefix(countryCode);
  const prefixLen = prefix ? prefix.toString().length : 0;
  if (prefixLen == 1 && value[0] == prefix) {
    return value.slice(1);
  } else if (prefixLen == 2 && value.slice(0,2) == prefix) {
    return value.slice(2);
  } else {
    return value;
  }
}

function getUnmaskedValue(input, countryCode) {
  const dialCode = input.dataset.dialCode || '';
  const value = input.value.replace(/[()\-# ]/g, '');

  const libDialCode = phoneDialCode(countryCode);
  if ('+' + libDialCode == dialCode) {
    return stripDoubleDialCode(value, libDialCode, countryCode);
  }
  return value.indexOf('+') === -1 ? dialCode + value : value;
}

/**
 * Handles the formWizard.retrieveFieldValue event to modify the return
 * value of the phone number to just be digits without parentheses etc.
 *
 * @param {CustomEvent} event
 */
function retrieveFieldValue(event) {
  const countryCode = getFieldValue(getForm(event.target), 'country_code') || 'US';
  event.detail.value = getUnmaskedValue(event.target, countryCode);
}

/**
 * Handles the input event to perform custom validation,
 * checking the regex against the phone number. We do it this custom way instead
 * of using the `pattern` HTML attribute, because the field value is modified
 * and doesn't match the actual visible text value of the input control.
 *
 * @param {CustomEvent} event
 */
function validateInput(event) {
  const form = getForm(event.target);
  const countryCode = getFieldValue(form, 'country_code') || 'US';

  if(!phoneNumberFormat(countryCode).test(getUnmaskedValue(event.target, countryCode))) {
    event.target.setCustomValidity('Please enter a valid phone number');
  } else {
    event.target.setCustomValidity('');
  }
}

export function setupPhoneNumberFields() {
  forEachSelector(selectors.phoneNumberField, setupPhoneNumberField);
}

addTargetEventListener(events.retrieveFieldValue, selectors.phoneNumberField, retrieveFieldValue);
addTargetEventListener('input', `${selectors.phoneNumberField} input`, validateInput);
addStartupScript(setupPhoneNumberFields);
